import type { EpisodeAvailability } from "@anisync/types";
import { CheckIcon, LanguagesIcon, PlusIcon } from "lucide-react";
import { useEffect, useState } from "react";
import { getEpisodes } from "../lib/api";
import { type NextEpisode, pickNextEpisode } from "../player/next-episode";
import { Badge } from "./ui/badge";
import { Button } from "./ui/button";

/**
 * Jedno kliknięcie: następny odcinek tej samej serii do kolejki.
 *
 * Panel „Dodaj do kolejki" umiał to od dawna, ale drogą przez wyszukanie tytułu
 * i wybór numeru z siatki — czyli trzy kroki na czynność, która w trakcie seansu
 * jest najczęstsza ze wszystkich. Tutaj numer bierze się z tego, co właśnie leci.
 *
 * **Odcinek nie musi być pobrany.** Kolejka przyjmuje pozycję bez materiału,
 * a serwer sam ją zdobywa: bieżącą przez `ensureCurrentMedia`, następną przez
 * przegląd prefetchu, gdy tylko bieżąca jest gotowa. Do czasu pobrania pozycja
 * czeka **zatrzymana** — czas płynie dopiero od dojścia do gotowości, inaczej
 * pokój wskoczyłby na dwudziestą minutę odcinka, którego nikt nie widział.
 *
 * Przycisku NIE MA, gdy następnego odcinka nie ma w żadnym źródle. To ta sama
 * zasada, co przy nakładce po zakończeniu odcinka: propozycja, która kończy się
 * tabelą prób, jest gorsza niż cisza. Dlatego liczy to `pickNextEpisode` —
 * wspólna funkcja, nie druga kopia tej reguły.
 */
export function RoomNextEpisode({
  titleId,
  titleLabel,
  episodeNumber,
  queuedEpisodes,
  onAdd,
}: {
  titleId: string | null;
  titleLabel: string;
  episodeNumber: number | null;
  /** Numery tej serii już stojące w kolejce — bez tego dokładalibyśmy duplikaty. */
  queuedEpisodes: number[];
  onAdd: (input: { titleId: string; episodeNumber: number }) => void;
}) {
  const [next, setNext] = useState<NextEpisode | null>(null);
  const [added, setAdded] = useState(false);

  useEffect(() => {
    if (titleId === null || episodeNumber === null) {
      setNext(null);
      return;
    }
    let cancelled = false;
    setAdded(false);
    setNext(null);

    /**
     * Pytamy o dostępność RAZ na zmianę odcinka, nie cyklicznie. To żądanie
     * uderza w źródła, a pokój i tak przeładuje ten panel przy każdym przejściu
     * dalej — częstsze odpytywanie byłoby natręctwem bez nowej informacji.
     */
    getEpisodes(titleId)
      .then((episodes: EpisodeAvailability[]) => {
        if (cancelled) return;
        // `episodesTotal` zostawiamy puste: siatka z serwera i tak nie zawiera
        // numerów spoza serii, a pobieranie metadanych tytułu byłoby drugim
        // żądaniem po jedno pole.
        setNext(pickNextEpisode(episodes, episodeNumber, null));
      })
      .catch(() => {
        // Brak odpowiedzi to brak propozycji. Panel jest dodatkiem do seansu,
        // nie jego warunkiem, więc milknie zamiast pokazywać błąd.
        if (!cancelled) setNext(null);
      });

    return () => {
      cancelled = true;
    };
  }, [titleId, episodeNumber]);

  if (titleId === null || next === null) return null;
  // Pozycja już zakolejkowana przez kogokolwiek — także przez drugą osobę
  // w pokoju, więc sam stan lokalny by tu nie wystarczył.
  const alreadyQueued = queuedEpisodes.includes(next.number);

  return (
    <div className="flex flex-wrap items-center gap-2 rounded-xl border border-border bg-card p-3">
      <div className="flex min-w-0 flex-1 flex-col gap-0.5">
        <span className="truncate text-sm font-medium">{titleLabel}</span>
        <span className="flex flex-wrap items-center gap-1.5 text-xs text-muted-foreground">
          Następny: odcinek {next.number}
          {/*
            Mówimy WPROST, czy to zagra od razu, czy trzeba poczekać na pobranie.
            Bez tego „dodaj" wygląda jak natychmiastowe, a bywa kilkuminutowe.
          */}
          {next.ready ? (
            <Badge variant="success">gotowy</Badge>
          ) : (
            <Badge variant="outline">do pobrania</Badge>
          )}
          {next.fallbackOnly && (
            <Badge variant="warning">
              <LanguagesIcon />
              tylko angielskie
            </Badge>
          )}
        </span>
      </div>

      <Button
        size="sm"
        variant={alreadyQueued || added ? "ghost" : "secondary"}
        disabled={alreadyQueued || added}
        onClick={() => {
          onAdd({ titleId, episodeNumber: next.number });
          // Kolejka wraca zdarzeniem z serwera z opóźnieniem, więc bez tego
          // znacznika nie wiadomo, czy kliknięcie w ogóle weszło.
          setAdded(true);
        }}
      >
        {alreadyQueued || added ? <CheckIcon /> : <PlusIcon />}
        {alreadyQueued || added ? "W kolejce" : "Dodaj następny"}
      </Button>
    </div>
  );
}
